// 🔥 가격 설정 문제 심층 디버깅 유틸리티 - 최고급 분석 도구
// [배경] 신규 모델 가격 설정이 반영되지 않는 문제를 단계별로 분석

import { collection, doc, getDocs, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { generatePriceId, generateModelId } from '../types/deviceCatalog';
import { getUnifiedPriceService } from '../services/unifiedPriceAdapter';

/**
 * 🔥 완전한 가격 데이터 흐름 추적 및 디버깅
 */
export const debugCompleteFlow = async (modelName: string, storage: string, connectivity = 'Wi-Fi') => {
  console.log('🔥 완전한 가격 데이터 흐름 디버깅 시작');
  console.log('==========================================');
  console.log(`🎯 대상: ${modelName} ${storage} ${connectivity}`);
  console.log(`📅 실행 시각: ${new Date().toISOString()}`);
  
  const results = {
    step1_deviceCatalog: null as any,
    step2_expectedPriceId: '',
    step3_unifiedPricesAll: [] as any[],
    step4_matchingPrices: [] as any[],
    step5_directQuery: null as any,
    step6_unifiedPriceService: null as any,
    summary: {
      modelExists: false,
      pricesFound: 0,
      matchingFound: false,
      serviceWorking: false,
      issues: [] as string[]
    }
  };
  
  try {
    // 🔍 1단계: device_catalog에서 모델 확인
    console.log('\n🔍 1단계: device_catalog 모델 확인...');
    
    const catalogSnapshot = await getDocs(collection(db, 'device_catalog'));
    const allCatalogModels = catalogSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    
    const targetModel = allCatalogModels.find((m: any) => 
      m.deviceType === 'tablet' && 
      m.brand === 'Apple' && 
      m.modelKo === modelName
    );
    
    if (targetModel) {
      console.log('✅ 모델 발견:', {
        id: targetModel.id,
        modelKo: targetModel.modelKo,
        series: targetModel.series,
        storageOptions: targetModel.storageOptions,
        connectivityOptions: targetModel.connectivityOptions,
        isActive: targetModel.isActive
      });
      results.step1_deviceCatalog = targetModel;
      results.summary.modelExists = true;
    } else {
      console.log('❌ 모델 없음');
      results.summary.issues.push('device_catalog에서 모델을 찾을 수 없음');
      return results;
    }
    
    // 🔍 2단계: 예상되는 priceId 생성
    console.log('\n🔍 2단계: 예상 priceId 생성...');
    
    const expectedPriceId = generatePriceId(
      targetModel.id,
      { 
        storage, 
        connectivity: connectivity !== 'Wi-Fi' ? connectivity : undefined 
      },
      'KR'
    );
    
    console.log(`🔑 예상 priceId: ${expectedPriceId}`);
    results.step2_expectedPriceId = expectedPriceId;
    
    // 🔍 3단계: unified_prices 전체 조회
    console.log('\n🔍 3단계: unified_prices 전체 조회...');
    
    const pricesSnapshot = await getDocs(collection(db, 'unified_prices'));
    const allPrices = pricesSnapshot.docs.map(doc => ({ 
      id: doc.id, 
      ...doc.data(),
      _docId: doc.id // 실제 문서 ID도 저장
    }));
    
    console.log(`📊 unified_prices 전체: ${allPrices.length}개`);
    
    const tabletPrices = allPrices.filter((p: any) => 
      p.deviceType === 'tablet' && p.brand === 'Apple'
    );
    
    console.log(`📱 Apple 태블릿 가격: ${tabletPrices.length}개`);
    results.step3_unifiedPricesAll = tabletPrices;
    results.summary.pricesFound = tabletPrices.length;
    
    // 🔍 4단계: 타겟 모델 관련 가격 찾기
    console.log('\n🔍 4단계: 타겟 모델 관련 가격 찾기...');
    
    const relatedPrices = tabletPrices.filter((p: any) => 
      p.modelId === targetModel.id || 
      p.id?.includes(targetModel.id) ||
      p._docId?.includes(targetModel.id)
    );
    
    console.log(`💰 관련 가격: ${relatedPrices.length}개`);
    
    if (relatedPrices.length > 0) {
      console.log('💰 발견된 관련 가격들:');
      relatedPrices.forEach((price: any) => {
        console.log(`  - 문서ID: ${price._docId}`);
        console.log(`    ID필드: ${price.id}`);
        console.log(`    modelId: ${price.modelId}`);
        console.log(`    variant: ${JSON.stringify(price.variant)}`);
        console.log(`    가격: A=${price.gradePrices?.A} B=${price.gradePrices?.B} C=${price.gradePrices?.C} D=${price.gradePrices?.D}`);
      });
      results.step4_matchingPrices = relatedPrices;
    } else {
      console.log('❌ 관련 가격 없음');
      results.summary.issues.push('unified_prices에서 관련 가격 데이터를 찾을 수 없음');
    }
    
    // 🔍 5단계: 예상 priceId로 직접 문서 조회
    console.log('\n🔍 5단계: 예상 priceId로 직접 문서 조회...');
    
    const directDoc = await getDoc(doc(db, 'unified_prices', expectedPriceId));
    
    if (directDoc.exists()) {
      const directData = directDoc.data();
      console.log('✅ 직접 문서 조회 성공:', {
        id: directDoc.id,
        data: directData
      });
      results.step5_directQuery = directData;
    } else {
      console.log(`❌ 직접 문서 조회 실패: ${expectedPriceId}`);
      results.summary.issues.push(`예상 priceId(${expectedPriceId})로 문서를 찾을 수 없음`);
    }
    
    // 🔍 6단계: UnifiedPriceService를 통한 조회
    console.log('\n🔍 6단계: UnifiedPriceService 조회...');
    
    try {
      const UnifiedPriceService = getUnifiedPriceService();
      const serviceResult = await UnifiedPriceService.getAllGradesPrice(
        targetModel.id, 
        storage, 
        connectivity
      );
      
      if (serviceResult && Object.values(serviceResult).some(p => p > 0)) {
        console.log('✅ UnifiedPriceService 조회 성공:', serviceResult);
        results.step6_unifiedPriceService = serviceResult;
        results.summary.serviceWorking = true;
      } else {
        console.log('❌ UnifiedPriceService 조회 실패 또는 가격 0');
        results.summary.issues.push('UnifiedPriceService가 가격을 조회하지 못함');
      }
    } catch (error) {
      console.log('❌ UnifiedPriceService 오류:', error);
      results.summary.issues.push(`UnifiedPriceService 오류: ${error}`);
    }
    
    // 🔍 7단계: 매칭 성공 확인
    if (relatedPrices.length > 0) {
      const exactMatch = relatedPrices.find((p: any) => 
        p.variant?.storage === storage &&
        (p.variant?.connectivity || 'Wi-Fi') === connectivity
      );
      
      if (exactMatch) {
        console.log('✅ 정확한 매칭 발견!', exactMatch);
        results.summary.matchingFound = true;
      } else {
        console.log('❌ 정확한 매칭 없음');
        results.summary.issues.push('storage/connectivity 매칭 실패');
      }
    }
    
    // 📊 최종 요약
    console.log('\n📊 최종 분석 요약:');
    console.log('==========================================');
    console.log(`✅ 모델 존재: ${results.summary.modelExists}`);
    console.log(`📊 발견된 가격: ${results.summary.pricesFound}개`);
    console.log(`🎯 정확한 매칭: ${results.summary.matchingFound}`);
    console.log(`🔧 서비스 작동: ${results.summary.serviceWorking}`);
    
    if (results.summary.issues.length > 0) {
      console.log('\n⚠️ 발견된 문제들:');
      results.summary.issues.forEach((issue, index) => {
        console.log(`  ${index + 1}. ${issue}`);
      });
    } else {
      console.log('\n🎉 모든 단계 정상 작동!');
    }
    
    return results;
    
  } catch (error) {
    console.error('❌ 완전 디버깅 실패:', error);
    results.summary.issues.push(`전체 디버깅 실패: ${error}`);
    return results;
  }
};

/**
 * 🔧 가격 데이터 수동 복구 함수
 */
export const repairPriceData = async (
  modelName: string, 
  storage: string, 
  connectivity = 'Wi-Fi',
  prices = { A: 1000000, B: 800000, C: 600000, D: 400000 }
) => {
  console.log('🔧 가격 데이터 수동 복구 시작');
  console.log(`🎯 대상: ${modelName} ${storage} ${connectivity}`);
  console.log(`💰 설정할 가격:`, prices);
  
  try {
    // 1단계: 모델 찾기
    const catalogSnapshot = await getDocs(collection(db, 'device_catalog'));
    const allModels = catalogSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    const targetModel = allModels.find((m: any) => 
      m.deviceType === 'tablet' && 
      m.brand === 'Apple' && 
      m.modelKo === modelName
    );
    
    if (!targetModel) {
      console.log('❌ 모델 없음');
      return false;
    }
    
    console.log(`✅ 모델 발견: ${targetModel.id}`);
    
    // 2단계: 정확한 priceId 생성
    const priceId = generatePriceId(
      targetModel.id,
      { 
        storage, 
        connectivity: connectivity !== 'Wi-Fi' ? connectivity : undefined 
      },
      'KR'
    );
    
    console.log(`🔑 생성된 priceId: ${priceId}`);
    
    // 3단계: 완전한 unified_prices 문서 생성
    const priceDoc = {
      id: priceId,
      modelId: targetModel.id,
      deviceType: 'tablet' as const,
      brand: 'Apple' as const,
      region: 'KR' as const,
      currency: 'KRW' as const,
      variant: {
        storage,
        ...(connectivity !== 'Wi-Fi' && { connectivity })
      },
      gradePrices: prices,
      isActive: true,
      version: 'live' as const,
      createdAt: new Date(),
      updatedAt: new Date(),
      updatedBy: 'manual-repair'
    };
    
    console.log('💾 저장할 완전한 문서:');
    console.log(JSON.stringify(priceDoc, null, 2));
    
    // 4단계: 직접 Firebase에 저장
    await setDoc(doc(db, 'unified_prices', priceId), priceDoc, { merge: false });
    console.log('✅ Firebase 직접 저장 완료');
    
    // 5단계: 저장 확인
    const verifyDoc = await getDoc(doc(db, 'unified_prices', priceId));
    if (verifyDoc.exists()) {
      const verifyData = verifyDoc.data();
      console.log('✅ 저장 확인 성공:', verifyData.gradePrices);
      
      // 6단계: UnifiedPriceService로 조회 테스트
      const UnifiedPriceService = getUnifiedPriceService();
      UnifiedPriceService.clearCache(); // 캐시 무효화
      
      const serviceResult = await UnifiedPriceService.getAllGradesPrice(
        targetModel.id, 
        storage, 
        connectivity
      );
      
      if (serviceResult && Object.values(serviceResult).some(p => p > 0)) {
        console.log('✅ UnifiedPriceService 조회도 성공:', serviceResult);
        console.log('🎉 가격 데이터 복구 완료!');
        return true;
      } else {
        console.log('❌ UnifiedPriceService 조회 여전히 실패');
        return false;
      }
    } else {
      console.log('❌ 저장 확인 실패');
      return false;
    }
    
  } catch (error) {
    console.error('❌ 가격 데이터 복구 실패:', error);
    return false;
  }
};

/**
 * 🔍 priceId 생성 로직 비교 테스트
 */
export const comparePriceIdGeneration = (modelName: string, storage: string, connectivity = 'Wi-Fi') => {
  console.log('🔍 priceId 생성 로직 비교 테스트');
  console.log(`🎯 대상: ${modelName} ${storage} ${connectivity}`);
  
  // 가상의 modelId 생성 (실제와 유사하게)
  const mockModelId = `apple-tablet-ipad-test-${modelName.toLowerCase().replace(/\s+/g, '-')}`;
  
  // 1. 공식 generatePriceId 함수
  const officialPriceId = generatePriceId(
    mockModelId,
    { 
      storage, 
      connectivity: connectivity !== 'Wi-Fi' ? connectivity : undefined 
    },
    'KR'
  );
  
  // 2. AddModelDialog에서 사용하던 방식 (이전)
  const addModelDialogId = `${mockModelId}-${storage.toLowerCase().replace(/[^a-z0-9]/g, '')}-${connectivity.toLowerCase().replace(/[^a-z0-9]/g, '')}-kr`;
  
  // 3. FirebasePriceManagementGrid에서 사용하던 방식 (이전)
  const managementGridId = `${mockModelId}-${storage.toLowerCase().replace(/[^a-z0-9]/g, '')}-${connectivity.toLowerCase().replace(/[^a-z0-9]/g, '')}-kr`;
  
  // 4. UnifiedPriceAdapter에서 시도하는 방식들
  const adapterMethod1 = `${mockModelId}-${storage.toLowerCase().replace(/[^a-z0-9]/g, '')}-${connectivity ? connectivity.toLowerCase().replace(/[^a-z0-9]/g, '') : 'wifi'}-kr`;
  
  console.log('🔑 생성된 priceId들 비교:');
  console.log(`  공식 함수:        ${officialPriceId}`);
  console.log(`  AddModelDialog:   ${addModelDialogId}`);
  console.log(`  ManagementGrid:   ${managementGridId}`);
  console.log(`  Adapter방식1:     ${adapterMethod1}`);
  
  // 일치 여부 확인
  const allSame = [officialPriceId, addModelDialogId, managementGridId, adapterMethod1].every(id => id === officialPriceId);
  
  if (allSame) {
    console.log('✅ 모든 방식이 일치함');
  } else {
    console.log('❌ priceId 생성 방식이 불일치함!');
    console.log('   → 이것이 문제의 원인일 가능성 높음');
  }
  
  return {
    official: officialPriceId,
    addDialog: addModelDialogId,
    managementGrid: managementGridId,
    adapter: adapterMethod1,
    allMatch: allSame
  };
};

/**
 * 🔥 Firebase 실제 저장 테스트
 */
export const testFirebaseWrite = async (
  modelName: string, 
  storage: string, 
  connectivity = 'Wi-Fi',
  prices = { A: 1200000, B: 1000000, C: 800000, D: 600000 }
) => {
  console.log('🔥 Firebase 실제 저장 테스트');
  console.log(`🎯 대상: ${modelName} ${storage} ${connectivity}`);
  
  try {
    // 1단계: 모델 찾기
    const catalogSnapshot = await getDocs(collection(db, 'device_catalog'));
    const allModels = catalogSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    const targetModel = allModels.find((m: any) => 
      m.deviceType === 'tablet' && 
      m.brand === 'Apple' && 
      m.modelKo === modelName
    );
    
    if (!targetModel) {
      console.log('❌ 모델 없음');
      return false;
    }
    
    // 2단계: 정확한 priceId 생성
    const priceId = generatePriceId(
      targetModel.id,
      { storage, connectivity: connectivity !== 'Wi-Fi' ? connectivity : undefined },
      'KR'
    );
    
    console.log(`🔑 사용할 priceId: ${priceId}`);
    
    // 3단계: 완전한 문서 구조로 저장
    const testDoc = {
      id: priceId,
      modelId: targetModel.id,
      deviceType: 'tablet' as const,
      brand: 'Apple' as const,
      region: 'KR' as const,
      currency: 'KRW' as const,
      variant: {
        storage,
        ...(connectivity !== 'Wi-Fi' && { connectivity })
      },
      gradePrices: prices,
      isActive: true,
      version: 'live' as const,
      createdAt: new Date(),
      updatedAt: new Date(),
      updatedBy: 'test-write'
    };
    
    console.log('💾 테스트 저장 시작...');
    await setDoc(doc(db, 'unified_prices', priceId), testDoc);
    console.log('✅ 테스트 저장 완료');
    
    // 4단계: 즉시 확인
    const checkDoc = await getDoc(doc(db, 'unified_prices', priceId));
    if (checkDoc.exists()) {
      const checkData = checkDoc.data();
      console.log('✅ 테스트 저장 확인:', checkData.gradePrices);
      
      // 5단계: UnifiedPriceService 조회 테스트
      const UnifiedPriceService = getUnifiedPriceService();
      UnifiedPriceService.clearCache();
      
      const serviceCheck = await UnifiedPriceService.getAllGradesPrice(
        targetModel.id, 
        storage, 
        connectivity
      );
      
      if (serviceCheck && Object.values(serviceCheck).some(p => p > 0)) {
        console.log('🎉 테스트 완전 성공!');
        console.log(`   저장 → 조회 → 서비스 모두 정상 작동`);
        return true;
      } else {
        console.log('❌ 서비스 조회는 여전히 실패');
        return false;
      }
    } else {
      console.log('❌ 테스트 저장 확인 실패');
      return false;
    }
    
  } catch (error) {
    console.error('❌ Firebase 저장 테스트 실패:', error);
    return false;
  }
};

// 전역 함수로 등록
if (typeof window !== 'undefined') {
  (window as any).debugCompleteFlow = debugCompleteFlow;
  (window as any).repairPriceData = repairPriceData;  
  (window as any).comparePriceIdGeneration = comparePriceIdGeneration;
  (window as any).testFirebaseWrite = testFirebaseWrite;
}
